import React from 'react';
import { ampelColor, deltaColor } from './KbsLayer';
import { trainGroupLabels, HIGH_DELAY_THRESHOLD_MIN } from './trainPosition';

const GROUP_ORDER = ['fern', 'nah', 'sbahn', 'sonstige', 'total'];
const MONTHS = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
const WEEKDAYS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

const fmtPct = (v) => (v === null || v === undefined ? '–' : `${v.toFixed(1)} %`);

const ppDiff = (today, prev) => {
  if (today === null || prev === null || today === undefined || prev === undefined) return null;
  const diff = today - prev;
  return { text: `${diff >= 0 ? '+' : ''}${diff.toFixed(1)} pp`, good: diff >= 0 };
};

const dayLabel = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  return `${WEEKDAYS[date.getDay()]} ${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}.`;
};

/** Horizontal bar in the style of the daily report's tables */
const Bar = ({ pct }) => (
  <div className="nd-bar">
    <div
      className="nd-bar-fill"
      style={{ width: `${Math.max(0, Math.min(100, pct || 0))}%`, background: ampelColor(pct) }}
    />
  </div>
);

/**
 * Network-wide statistics for the operating day the map is showing — the daily operations
 * report as a dashboard. Always for the whole network, never per train or station: those
 * two have their own panels.
 */
const NetworkDashboard = ({ stats, loading, opDay, open, onToggle }) => {
  const groups = stats?.groups;
  const daily = stats?.trend?.daily || [];
  const monthly = stats?.trend?.monthly || [];

  return (
    <section className="network-dashboard">
      <button className="nd-header" onClick={onToggle}>
        <i className={`fa fa-chevron-${open ? 'down' : 'right'}`}></i>
        <span className="nd-title"><i className="fa fa-bar-chart"></i> Tagesauswertung</span>
        <span className="nd-sub">Betriebstag {opDay} · 04:00–04:00 · gesamtes Netz</span>
        {groups?.total && (
          <span className="nd-headline" style={{ color: ampelColor(groups.total.on_time_pct) }}>
            {fmtPct(groups.total.on_time_pct)} pünktlich
          </span>
        )}
      </button>

      {open && loading && (
        <div className="nd-loading"><i className="fa fa-spinner fa-spin"></i> Auswertung wird gerechnet…</div>
      )}

      {open && stats && (
        <div className="nd-body">
          {/* Volume and punctuality per traffic group */}
          <div className="nd-block nd-block-wide">
            <h5>Verkehr und Pünktlichkeit</h5>
            <table className="nd-table">
              <thead>
                <tr>
                  <th>Verkehr</th>
                  <th className="num">Züge</th>
                  <th>Pünktlichkeit</th>
                  <th className="num">Anteil</th>
                  <th className="num">Median</th>
                  <th className="num">Ø Versp.</th>
                  <th className="num" title={`ab ${HIGH_DELAY_THRESHOLD_MIN} min`}>≥60 min</th>
                  <th className="num">Gleisänd.</th>
                </tr>
              </thead>
              <tbody>
                {GROUP_ORDER.map(group => {
                  const m = groups[group];
                  if (!m || m.count === 0) return null;
                  const diff = ppDiff(m.on_time_pct, stats.prevGroups?.[group]?.on_time_pct);
                  return (
                    <tr key={group} className={group === 'total' ? 'total-row' : ''}>
                      <td>{group === 'total' ? 'Gesamt' : trainGroupLabels[group]}</td>
                      <td className="num">{m.count}</td>
                      <td><Bar pct={m.on_time_pct} /></td>
                      <td className="num">
                        {fmtPct(m.on_time_pct)}
                        {diff && <span className={`nd-pp ${diff.good ? 'up' : 'down'}`}>{diff.text}</span>}
                      </td>
                      <td className="num">{m.delay_median} min</td>
                      <td className="num">{m.delay_avg.toFixed(1)} min</td>
                      <td className="num">{m.high_delay || '–'}</td>
                      <td className="num">{m.track_changes || '–'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <p className="nd-note">
              Netzsummen zählen pro Zug den Wert am Ziel — ein Zug, der Verspätung wieder aufholt,
              gilt hier als pünktlich. Die Bahnhofs-Statistik zählt dagegen jeden Halt einzeln.
            </p>
          </div>

          {/* Seven day trend */}
          <div className="nd-block">
            <h5>Pünktlichkeit letzte 7 Betriebstage <em>(Fernverkehr)</em></h5>
            <table className="nd-table">
              <tbody>
                {daily.map(d => (
                  <tr key={d.date}>
                    <td className="nd-label">{dayLabel(d.date)}</td>
                    <td><Bar pct={d.on_time_pct} /></td>
                    <td className="num">{fmtPct(d.on_time_pct)}</td>
                    <td className="num nd-muted">{d.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Monthly trend */}
          <div className="nd-block">
            <h5>Pünktlichkeit letzte Monate <em>(Fernverkehr)</em></h5>
            <table className="nd-table">
              <tbody>
                {monthly.map(m => (
                  <tr key={`${m.year}-${m.month}`}>
                    <td className="nd-label">{MONTHS[m.month - 1]} {m.year}</td>
                    <td><Bar pct={m.on_time_pct} /></td>
                    <td className="num">{fmtPct(m.on_time_pct)}</td>
                    <td className="num nd-muted">{m.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {monthly.some(m => m.sampled) && (
              <p className="nd-note">Monatswerte aus Stichtagen gerechnet, nicht aus allen Tagen.</p>
            )}
          </div>

          {/* Worst trains */}
          <div className="nd-block">
            <h5>Verspätetste Züge <em>(Verspätung am Ziel)</em></h5>
            <ol className="nd-list">
              {stats.topDelayed.slice(0, 8).map((t, i) => (
                <li key={`${t.trainName}-${i}`}>
                  <span className="nd-list-name">{t.trainName}</span>
                  <span className="nd-list-meta">{t.terminus} · {t.sched} → {t.act}</span>
                  <span className="nd-list-value late">+{t.delay} min</span>
                </li>
              ))}
              {stats.topDelayed.length === 0 && <li className="nd-muted">Keine Verspätungen</li>}
            </ol>
          </div>

          {/* Worst KBS */}
          <div className="nd-block">
            <h5>Schwächste Kursbuchstrecken</h5>
            <ol className="nd-list">
              {stats.worstKbs.map(k => (
                <li key={k.kbs_number}>
                  <span className="nd-list-name">KBS {k.kbs_number}</span>
                  <span className="nd-list-meta">{k.kbs_name}</span>
                  <span className="nd-list-value" style={{ color: ampelColor(k.on_time_pct) }}>
                    {fmtPct(k.on_time_pct)}
                  </span>
                </li>
              ))}
              {stats.worstKbs.length === 0 && <li className="nd-muted">Keine Strecke mit genug Zügen</li>}
            </ol>
          </div>

          {/* Where delay is picked up */}
          <div className="nd-block">
            <h5>Größter Verspätungszuwachs je Abschnitt</h5>
            <ol className="nd-list">
              {stats.worstSegments.map(s => (
                <li key={`${s.kbs_number}-${s.section}`}>
                  <span className="nd-list-name">{s.section}</span>
                  <span className="nd-list-meta">KBS {s.kbs_number} · {s.count} Züge</span>
                  <span className="nd-list-value" style={{ color: deltaColor(s.delta_avg_min) }}>
                    {s.delta_avg_min >= 0 ? '+' : ''}{s.delta_avg_min.toFixed(1)} min
                  </span>
                </li>
              ))}
              {stats.worstSegments.length === 0 && <li className="nd-muted">Keine Daten</li>}
            </ol>
          </div>

          {/* Reasons */}
          <div className="nd-block">
            <h5>Häufigste Meldungen</h5>
            <ol className="nd-list">
              {stats.reasons.map(r => (
                <li key={r.messageText}>
                  <span className="nd-list-name">{r.messageText}</span>
                  <span className="nd-list-meta">Priorität {r.priority}</span>
                  <span className="nd-list-value">{r.affected_trains} Züge</span>
                </li>
              ))}
              {stats.reasons.length === 0 && <li className="nd-muted">Keine Meldungen</li>}
            </ol>
          </div>

          {/* Cancellations */}
          <div className="nd-block nd-block-small">
            <h5>Ausfälle</h5>
            <div className="nd-big" style={{ color: stats.cancelled > 0 ? 'var(--primary-color)' : 'var(--accent-green)' }}>
              {stats.cancelled}
            </div>
            {(() => {
              const diff = stats.cancelled - (stats.prevCancelled ?? stats.cancelled);
              if (diff === 0) return <p className="nd-note">unverändert zum Vortag</p>;
              return (
                <p className="nd-note">
                  {diff > 0 ? `${diff} mehr` : `${-diff} weniger`} als am Vortag
                </p>
              );
            })()}
          </div>
        </div>
      )}
    </section>
  );
};

export default NetworkDashboard;
